import { useEffect, useMemo, useState } from "react";
import { SortingState } from "@tanstack/react-table";
import useToneOfVoice from "@/hooks/useToneOfVoice";
import ToneOfVoice from "@/models/tone-of-voice/toneOfVoice";
import { debounce } from "lodash";

interface UseToneOfVoiceTableDataReturn {
  data: ToneOfVoice[];
  search: string;
  pageCount: number;
  pageIndex: number;
  pageSize: number;
  sorting: SortingState;
  setPageIndex: (pageIndex: number) => void;
  setPageSize: (pageSize: number) => void;
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
  handleSearchChange: (searchValue: string) => void;
  isLoading: boolean;
}

const useToneOfVoiceTableData = (): UseToneOfVoiceTableDataReturn => {
  const { fetchToneOfVoices } = useToneOfVoice();
  // Data state
  const [data, setData] = useState<ToneOfVoice[]>([]);
  const [pageCount, setPageCount] = useState(0);

  // Pagination state
  const [pageIndex, setPageIndex] = useState(0); // zero-based index
  const [pageSize, setPageSize] = useState(20);

  // Sorting state
  const [sorting, setSorting] = useState<SortingState>([
    { id: "isDefault", desc: true },
  ]);

  // Search state
  const [input, setInput] = useState(""); // For capturing immediate user input
  const [search, setSearch] = useState(""); // For the actual debounced search

  // Abort controller state
  const [abortController, setAbortController] =
    useState<AbortController | null>(null);

  const fetchedToneOfVoices = fetchToneOfVoices({
    pageNumber: pageIndex + 1,
    pageSize,
    sortAscending: sorting.length ? !sorting[0].desc : true,
    sortBy: sorting.length ? sorting[0].id : "",
    search,
    filters: [],
  });

  // Effects
  useEffect(() => {
    if (
      fetchedToneOfVoices.isFetched &&
      fetchedToneOfVoices.data &&
      fetchedToneOfVoices.data.data
    ) {
      setData(fetchedToneOfVoices.data.data);
      setPageCount(fetchedToneOfVoices.data.totalPages);
    }
  }, [fetchedToneOfVoices.data, fetchedToneOfVoices.isFetched]);

  useEffect(() => {
    const debouncedFetch = debounce((searchValue: string) => {
      if (abortController) {
        abortController.abort();
      }

      const controller = new AbortController();
      setAbortController(controller);

      setPageIndex(0);
      setSearch(searchValue);
    }, 300);

    debouncedFetch(input);

    return () => {
      debouncedFetch.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  const handleSearchChange = (searchValue: string) => {
    setInput(searchValue);
  };

  return {
    data: useMemo(() => data, [data]),
    search: input,
    pageCount,
    pageIndex,
    pageSize,
    sorting,
    setPageIndex,
    setPageSize,
    setSorting,
    handleSearchChange,
    isLoading: fetchedToneOfVoices.isPending,
  };
};

export default useToneOfVoiceTableData;
